import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { ReelMetadata } from "../../types";

interface GridMotionHeaderProps {
  data: ReelMetadata;
  opacity: number;
  scale: number;
  translateY: number;
}

export const GridMotionHeader: React.FC<GridMotionHeaderProps> = ({
  data,
  opacity,
  scale,
  translateY,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Convert frame to tech-style indicators
  const hexFrame = `0x${frame.toString(16).toUpperCase().padStart(4, "0")}`;
  const seconds = (frame / fps).toFixed(2);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        padding: "90px 45px 0",
        zIndex: 10,
        opacity,
        transform: `scale(${scale}) translateY(${translateY}px)`,
        boxSizing: "border-box",
      }}
    >
      {/* Tech Info Bar with pulsing dot and hex frame counter */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "480px",
          background: "rgba(4, 5, 15, 0.75)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(0, 242, 254, 0.25)",
          borderRadius: "8px",
          padding: "8px 16px",
          marginBottom: "20px",
          fontFamily: "var(--font-title)",
          fontSize: "12px",
          letterSpacing: "1.5px",
          color: "rgba(255, 255, 255, 0.6)",
          boxShadow: "0 0 15px rgba(0, 242, 254, 0.1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* Pulsing indicator dot */}
          <span
            style={{
              width: "6px",
              height: "6px",
              background: "#bef264", // Lime green
              borderRadius: "50%",
              boxShadow: "0 0 8px #bef264",
              display: "inline-block",
            }}
          />
          <span style={{ fontWeight: 800, color: "#bef264" }}>GRID_STREAM</span>
        </div>

        {/* Live counter */}
        <div style={{ display: "flex", gap: "12px", fontFamily: "monospace" }}>
          <span>{hexFrame}</span>
          <span style={{ color: "rgba(255, 255, 255, 0.3)" }}>|</span>
          <span style={{ color: "#00f2fe" }}>SEC_{seconds}</span>
        </div>
      </div>

      {/* Cyberpunk Bracketed Header Box */}
      <div
        style={{
          position: "relative",
          width: "100%",
          padding: "20px 24px",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Cyber corners */}
        {/* Top-left */}
        <div style={{ position: "absolute", top: 0, left: 24, width: "16px", height: "16px", borderLeft: "2px solid #00f2fe", borderTop: "2px solid #00f2fe" }} />
        {/* Top-right */}
        <div style={{ position: "absolute", top: 0, right: 24, width: "16px", height: "16px", borderRight: "2px solid #ff007f", borderTop: "2px solid #ff007f" }} />
        {/* Bottom-left */}
        <div style={{ position: "absolute", bottom: 0, left: 24, width: "16px", height: "16px", borderLeft: "2px solid #bef264", borderBottom: "2px solid #bef264" }} />
        {/* Bottom-right */}
        <div style={{ position: "absolute", bottom: 0, right: 24, width: "16px", height: "16px", borderRight: "2px solid #9d4ede", borderBottom: "2px solid #9d4ede" }} />

        {/* Headline Text */}
        <h1
          style={{
            fontFamily: "var(--font-title)",
            fontSize: "56px",
            fontWeight: 900,
            background: "linear-gradient(135deg, #ffffff 40%, #00f2fe 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textAlign: "center",
            margin: 0,
            lineHeight: 1.1,
            letterSpacing: "-1px",
            textShadow: "0 0 25px rgba(0, 242, 254, 0.15)",
          }}
        >
          {data.headline}
        </h1>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
